import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import movie from "../assets/lankacinema.png";
import ecommerce2 from "../assets/lankahomes.png";
import system1 from "../assets/car_service.jpg";
import jobNest from "../assets/jobnest.png";
import portfolio from "../assets/portfolio.png";
import gant from "../assets/gant.png";
import autoGenius from "../assets/autogenius.png";
import bananaCalculus from "../assets/bananacalculus.png";
import eventSystem from "../assets/eventmanagementsystem.png";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";
import SectionHeader from "./SectionHeader";
import { fadeUpItem, staggerContainer, viewportOnce } from "../motionPresets";

const Projects = () => {
  const reduced = useReducedMotion();
  const projects = [
    {
      id: 1,
      title: "AutoGenius Invest",
      description:
        "Multi-agent AI portfolio optimizer — real-time stock analysis with AutoGen and OpenAI GPT-4.",
      detailedDescription: [
        "Python / Flask backend with market data workflows",
        "React Native client for guided analysis and recommendations",
        "Node.js services, MongoDB storage, and JWT-secured access",
      ],
      image: autoGenius,
      tech: ["Python", "Flask", "React Native", "Node.js", "MongoDB", "AutoGen", "OpenAI GPT-4", "JWT"],
      githubLink: "https://github.com/m-nasmi/AutoGeniusInvest.git",
      liveLink: null,
      isSpotlight: true,
    },
    {
      id: 2,
      title: "Job-Nest",
      description: "Job search platform with real-time chat and secure authentication.",
      detailedDescription: [
        "React SPA with Tailwind-driven UI",
        "Firebase for auth and real-time messaging",
        "Node.js APIs supporting search and listings",
      ],
      image: jobNest,
      tech: ["React", "Node.js", "Firebase", "Tailwind CSS"],
      githubLink: "https://github.com/m-ahmed-anwer/Job-Nest.git",
      liveLink: null,
      isFeatured: true,
    },
    {
      id: 3,
      title: "Car Service System",
      description: "End-to-end platform for vehicle service tracking and scheduling.",
      detailedDescription: [
        "React dashboard with Tailwind styling",
        "Spring Boot REST APIs and MySQL persistence",
        "Workflows for bookings, status, and history",
      ],
      image: system1,
      tech: ["React", "Spring Boot", "MySQL", "Tailwind CSS"],
      githubLink: "https://github.com/m-nasmi/CarService.git",
      liveLink: null,
      isFeatured: true,
    },
    {
      id: 4,
      title: "Banana Calculus",
      description: "Gamified mobile math game with accounts and leaderboards.",
      detailedDescription: [
        "React Native app with JWT-secured profiles",
        "Node.js backend and MongoDB for scores and rankings",
        "Modular structure ready to extend with new levels",
      ],
      image: bananaCalculus,
      tech: ["React Native", "Node.js", "MongoDB", "JWT"],
      githubLink: "https://github.com/m-nasmi/Banana_Calculus.git",
      liveLink: null,
      isFeatured: true,
    },
    {
      id: 5,
      title: "Event Management System",
      description: "Full-stack CRUD platform for planning and managing events.",
      detailedDescription: [
        "Spring Boot REST layer and MySQL",
        "React frontend with Tailwind CSS",
        "Responsive layouts for admin and attendee views",
      ],
      image: eventSystem,
      tech: ["React", "Spring Boot", "MySQL", "Tailwind CSS"],
      githubLink: "https://github.com/m-nasmi/Event-Management-System.git",
      liveLink: null,
      isFeatured: true,
    },
    {
      id: 6,
      title: "Lanka Homes",
      description: "E-commerce style storefront for browsing homes and property listings.",
      image: ecommerce2,
      tech: ["HTML", "CSS", "PHP", "MySQL"],
      githubLink: "https://github.com/m-nasmi/WAD-Lanka.git",
      liveLink: null,
      isFeatured: true,
    },
    {
      id: 7,
      title: "Movie Web App",
      description: "Responsive movie explorer powered by external APIs.",
      image: movie,
      tech: ["React", "Tailwind CSS"],
      githubLink: "https://github.com/m-nasmi/React-Movie.git",
      liveLink: null,
    },
    {
      id: 8,
      title: "Personal Portfolio",
      description: "This portfolio — React, Tailwind, and smooth section navigation.",
      image: portfolio,
      tech: ["React", "Tailwind CSS"],
      githubLink: "https://github.com/m-nasmi/Portfolio.git",
      liveLink: "https://muhammednasmi.netlify.app/",
    },
    {
      id: 9,
      title: "GANT Mobile App",
      description: "Mobile game prototype with authentication flows.",
      image: gant,
      tech: ["React Native", "Node.js"],
      githubLink: "https://github.com/NadeepaRavindu/GANT.git",
      liveLink: null,
    },
  ];

  const spotlight = projects.filter((p) => p.isSpotlight);
  const featured = projects.filter((p) => p.isFeatured && !p.isSpotlight);
  const other = projects.filter((p) => !p.isFeatured && !p.isSpotlight);

  const ProjectActions = ({ githubLink, liveLink, className = "" }) => (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <a
        href={githubLink || "#"}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          githubLink
            ? "bg-sky-600 hover:bg-sky-500 text-white shadow-md shadow-sky-900/30"
            : "bg-slate-800 text-slate-500 cursor-not-allowed pointer-events-none"
        }`}
      >
        <FaGithub /> Code
      </a>
      {liveLink && (
        <a
          href={liveLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-white/[0.12] text-slate-200 hover:bg-white/[0.06] hover:border-sky-500/25 transition-colors"
        >
          <FaExternalLinkAlt /> Live
        </a>
      )}
    </div>
  );

  return (
    <div
      name="projects"
      className="w-full border-t border-white/[0.06] bg-[#030712] text-white pt-20 pb-20 px-4 sm:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          eyebrow="Portfolio"
          title="Selected projects"
          subtitle="Representative work spanning AI-assisted tooling, mobile apps, and full-stack web platforms."
        />

        {spotlight.map((project) => (
          <motion.div
            key={project.id}
            className="mb-16"
            initial={reduced ? false : { opacity: 0, y: 28 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: reduced ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-sky-500 text-[11px] font-semibold tracking-[0.2em] uppercase mb-4 text-center">
              Spotlight
            </p>
            <div className="relative rounded-3xl overflow-hidden border border-sky-500/25 bg-gradient-to-br from-sky-500/10 via-slate-900/90 to-indigo-600/10 p-px shadow-[0_0_48px_-12px_rgba(56,189,248,0.35)] ring-1 ring-white/[0.06]">
              <div className="rounded-[1.2rem] bg-slate-950/90 backdrop-blur-md md:flex overflow-hidden">
                <div className="md:w-2/5 min-h-[220px] bg-slate-900/40 flex items-center justify-center p-6 border-b md:border-b-0 md:border-r border-white/[0.06]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="max-h-56 w-full object-contain"
                  />
                </div>
                <div className="p-8 md:w-3/5 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-slate-400 text-lg mb-4 leading-relaxed">{project.description}</p>
                  {project.detailedDescription && (
                    <ul className="mb-6 pl-5 list-disc text-slate-400 space-y-1 text-sm md:text-base marker:text-sky-500/80">
                      {project.detailedDescription.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="bg-sky-500/10 text-sky-200/90 text-xs font-medium px-3 py-1 rounded-lg border border-sky-500/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <ProjectActions githubLink={project.githubLink} liveLink={project.liveLink} />
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        <motion.div
          className="mb-12"
          variants={staggerContainer}
          initial={reduced ? false : "hidden"}
          animate={reduced ? "visible" : undefined}
          whileInView={reduced ? undefined : "visible"}
          viewport={viewportOnce}
        >
          <h3 className="text-lg font-semibold mb-6 text-white tracking-tight border-l-2 border-sky-500 pl-3">
            More highlights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featured.map((project) => (
              <motion.div
                key={project.id}
                variants={fadeUpItem}
                whileHover={reduced ? undefined : { y: -4 }}
                transition={{ duration: 0.22 }}
                className="rounded-2xl border border-white/[0.08] bg-slate-900/30 backdrop-blur-sm overflow-hidden flex flex-col hover:border-sky-500/20 transition-colors shadow-lg shadow-black/25 ring-1 ring-white/[0.04]"
              >
                <div className="h-44 bg-slate-900/50 flex items-center justify-center p-4 border-b border-white/[0.06]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="max-h-full w-full object-contain"
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-semibold text-white mb-2 tracking-tight">{project.title}</h3>
                  <p className="text-slate-400 text-sm mb-3 flex-grow leading-relaxed">{project.description}</p>
                  {project.detailedDescription && (
                    <ul className="text-slate-500 text-xs space-y-1 mb-4 list-disc list-inside">
                      {project.detailedDescription.slice(0, 2).map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="bg-slate-950/80 text-sky-300/90 text-xs font-medium px-2 py-0.5 rounded-md border border-white/[0.06]"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <ProjectActions githubLink={project.githubLink} liveLink={project.liveLink} />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial={reduced ? false : "hidden"}
          animate={reduced ? "visible" : undefined}
          whileInView={reduced ? undefined : "visible"}
          viewport={viewportOnce}
        >
          <h3 className="text-lg font-semibold mb-6 text-white tracking-tight">Other projects</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {other.map((project) => (
              <motion.div
                key={project.id}
                variants={fadeUpItem}
                whileHover={reduced ? undefined : { y: -3 }}
                transition={{ duration: 0.2 }}
                className="rounded-xl border border-white/[0.08] bg-slate-900/25 overflow-hidden flex flex-col h-full hover:bg-slate-900/45 hover:border-sky-500/15 transition-colors ring-1 ring-white/[0.03]"
              >
                <div className="h-40 bg-slate-900/50 flex items-center justify-center p-3 border-b border-white/[0.05]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="max-h-full w-full object-contain"
                  />
                </div>
                <div className="p-4 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-slate-500 text-sm mb-4 flex-grow leading-relaxed">{project.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="text-[11px] text-sky-300/85 bg-sky-500/10 px-2 py-0.5 rounded-md font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <ProjectActions githubLink={project.githubLink} liveLink={project.liveLink} />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects;
